// Dates here are **calendar dates with no zone** (DATE-3), carried as a Date
// pinned to UTC midnight so that arithmetic on them is exact. `weekStart` is the
// Monday of such a week.
//
// That representation was never the bug. The bug was *deriving today from it*:
// `new Date()` reduced to a UTC calendar date is **yesterday** for anyone in
// UTC+10/+11 until 10:00 or 11:00 local. So every Sydney morning, the real
// current week resolved to "next week" and was rejected as `FUTURE`, and last
// week's sheet was the one on screen. It also silently shifted the overdue
// deadline and — because invoice periods are built out of weeks — what a week is
// worth.
//
// `businessDate()` below is the single answer to "what day is it", and everything
// that needs to know defaults to it (DATE-1, DATE-4).

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DAY_MS = 24 * 60 * 60 * 1000;

/** Strip a date down to UTC midnight. */
export function utcDate(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export function addDays(d: Date, n: number): Date {
  const out = utcDate(d);
  out.setUTCDate(out.getUTCDate() + n);
  return out;
}

/** The Monday (UTC midnight) of the week containing `d`. */
export function startOfWeek(d: Date): Date {
  const date = utcDate(d);
  const day = date.getUTCDay(); // 0 Sun .. 6 Sat
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(date, diff);
}

/** The seven UTC-midnight dates Mon..Sun of the week starting at `monday`. */
export function weekDays(monday: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/**
 * Saturday or Sunday.
 *
 * The weekend used to be "index ≥ 5 in a Monday-first array" — true, but only
 * inside a structure that happens to start on Monday. A rule that has to answer
 * "is *this date* a weekend" (a note requirement, a default entry type) cannot
 * borrow that, and re-deriving it per caller is how the two drift.
 */
export function isWeekend(d: Date): boolean {
  const day = d.getUTCDay(); // 0 Sun .. 6 Sat
  return day === 0 || day === 6;
}

/** ISO 8601 week number. */
export function isoWeek(d: Date): number {
  const date = utcDate(d);
  const dayNum = (date.getUTCDay() + 6) % 7; // Mon=0
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  return 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * DAY_MS));
}

/** e.g. "Mon 04 May" */
export function formatDay(d: Date): string {
  return `${WEEKDAYS[d.getUTCDay()]} ${String(d.getUTCDate()).padStart(2, "0")} ${
    MONTHS[d.getUTCMonth()]
  }`;
}

/** e.g. "04 May – 10 May 2026" */
export function weekRange(monday: Date): string {
  const sunday = addDays(monday, 6);
  const a = `${String(monday.getUTCDate()).padStart(2, "0")} ${MONTHS[monday.getUTCMonth()]}`;
  const b = `${String(sunday.getUTCDate()).padStart(2, "0")} ${MONTHS[sunday.getUTCMonth()]}`;
  return `${a} – ${b} ${sunday.getUTCFullYear()}`;
}

/** e.g. "Wk 19 · 04 May – 10 May 2026" */
export function weekLabel(monday: Date): string {
  return `Wk ${isoWeek(monday)} · ${weekRange(monday)}`;
}

/** ISO date key, e.g. "2026-05-04". Handy for stable keys and input values. */
export function ymd(d: Date): string {
  return utcDate(d).toISOString().slice(0, 10);
}

/** Parse a "2026-05-04" string into a UTC-midnight Date. */
export function fromYmd(s: string): Date {
  const [y, m, day] = s.split("-").map(Number);
  return new Date(Date.UTC(y ?? NaN, (m ?? NaN) - 1, day ?? NaN));
}

/** True when `s` parses to a valid UTC date that round-trips (rejects 2026-02-31). */
export function isValidYmd(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = fromYmd(s);
  return !Number.isNaN(d.getTime()) && ymd(d) === s;
}

/**
 * The business timezone (DATE-1). Every week and day boundary resolves in it.
 *
 * Pending confirmation — PRD O-4. It is defined once, here, so confirming it is a
 * one-line change rather than an archaeology exercise.
 */
export const BUSINESS_TIMEZONE = "Australia/Sydney";

/**
 * Formatters, kept rather than rebuilt.
 *
 * Constructing an `Intl.DateTimeFormat` is genuinely expensive — it resolves a
 * locale and a timezone database entry — and `businessDate()` is the default
 * argument of `weekEditability`, `submissionState` and `chaseableWeek`, so it is
 * called once *per row* on paths that loop over weeks × assignments. Building the
 * formatter per call put a few thousand of them in one chase request.
 *
 * Keyed by zone because the parameter exists and the tests use it.
 */
const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = zoneFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    zoneFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/**
 * The calendar date it currently is in the business timezone.
 *
 * Returned as a zoneless calendar date (UTC-midnight Date, per DATE-3), which is
 * the same shape every other date in this module has — so it drops straight into
 * `startOfWeek`, `addDays`, and the comparisons in rules.ts.
 *
 * `Intl` does the zone conversion, including DST, which Sydney observes. `en-CA`
 * is the locale that formats as YYYY-MM-DD.
 *
 * Cheap to call, but **a request should still resolve it once and pass it down**.
 * The default argument is for callers with no opinion; a caller that asks twice
 * has two opinions, and across a midnight boundary they can differ.
 */
export function businessDate(
  instant: Date = new Date(),
  timeZone: string = BUSINESS_TIMEZONE
): Date {
  return fromYmd(zoneFormatter(timeZone).format(instant));
}

const hourFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * The hour (0–23) it currently is in the business timezone — for a timer that
 * must act at a local hour, which NCRONTAB cannot be told without moving the
 * whole process's zone (see functions/reminders.ts). DST included, via `Intl`.
 */
export function businessHour(
  instant: Date = new Date(),
  timeZone: string = BUSINESS_TIMEZONE
): number {
  let formatter = hourFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      hour: "2-digit",
      hourCycle: "h23",
    });
    hourFormatters.set(timeZone, formatter);
  }
  return Number(formatter.format(instant));
}

/**
 * A zoneless calendar date the way an Australian reads it: "24/09/2026".
 *
 * Built from the ISO key rather than `Intl`, because the date carries no zone to
 * convert — formatting it through a timezone is how a date slides a day.
 */
export function auDate(d: Date): string {
  const [year, month, day] = ymd(d).split("-");
  return `${day}/${month}/${year}`;
}

const auDateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * An instant as the business's wall clock: "24/09/2026 14:05", 24-hour.
 *
 * Unlike `auDate`, this *is* a zone conversion — a `submittedAt` is a moment,
 * and a moment has to be read on somebody's clock. It is the business's
 * (DATE-1), DST included, so an AEDT evening does not appear as the next
 * morning in UTC. Laid out so Excel with an Australian locale reads it as a
 * date-time rather than text.
 */
export function auDateTime(instant: Date, timeZone: string = BUSINESS_TIMEZONE): string {
  let formatter = auDateTimeFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-AU", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    auDateTimeFormatters.set(timeZone, formatter);
  }
  const part = Object.fromEntries(
    formatter.formatToParts(instant).map((p) => [p.type, p.value])
  );
  return `${part.day}/${part.month}/${part.year} ${part.hour}:${part.minute}`;
}

/** The Monday of the week we are actually in, business-local. */
export function currentBusinessWeek(instant: Date = new Date()): Date {
  return startOfWeek(businessDate(instant));
}

/**
 * The Monday of the most recent week that has actually ended.
 *
 * The default period for anything that asks "who is late": the *current* week
 * hasn't ended, so nothing in it is due yet (TIME-10), and a chase list full of
 * not-yet-late rows trains people to ignore it.
 */
export function lastCompletedWeek(today: Date = businessDate()): Date {
  return addDays(startOfWeek(today), -7);
}

/**
 * Turn a `from`/`to` filter into the Monday range that *overlaps* it.
 *
 * Both sides snap outward to their own week's Monday, so a range starting
 * mid-week still includes the week it starts in. Filtering `weekStart >= from`
 * directly would silently drop the week straddling the boundary — the one most
 * likely to be the reason someone picked that date. Snapping also keeps the
 * comparison on `weekStart` itself, so it stays indexable.
 *
 * It is a coarse bound on *periods*, not an exact one: both halves of a week
 * split at a month end share its Monday (TIME-1), so a range from the 1st still
 * reaches the previous month's half. Callers filtering timesheet rows add the
 * period overlap (`periodEnd >= from`, `periodStart <= to`) on top, as
 * `lib/timesheet-filter.ts` does.
 *
 * Null on either side means unbounded.
 */
export function weekRangeBounds(
  from?: string | null,
  to?: string | null
): { firstMonday: Date | null; lastMonday: Date | null } {
  return {
    firstMonday: from ? startOfWeek(fromYmd(from)) : null,
    lastMonday: to ? startOfWeek(fromYmd(to)) : null,
  };
}

/**
 * Every Monday from `firstMonday` to `lastMonday` inclusive.
 *
 * The week axis for anything that has to reason about weeks with no rows — a
 * chase list is a left-anti-join whose right-hand side has no table. Callers
 * bound the span themselves (see `MAX_CHASE_WEEKS`); this does not.
 */
export function mondaysBetween(firstMonday: Date, lastMonday: Date): Date[] {
  const out: Date[] = [];
  for (
    let m = startOfWeek(firstMonday);
    m.getTime() <= startOfWeek(lastMonday).getTime();
    m = addDays(m, 7)
  ) {
    out.push(m);
  }
  return out;
}

/** All Mondays of weeks that overlap the month containing `d` (for month views). */
export function mondaysOfMonth(d: Date): Date[] {
  const first = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
  const mondays: Date[] = [];
  for (let m = startOfWeek(first); m.getTime() <= last.getTime(); m = addDays(m, 7)) {
    mondays.push(m);
  }
  return mondays;
}

/**
 * A `YYYY-MM` month key naming a real month.
 *
 * The year is bounded because an unbounded one turns a typo into a query: a
 * report for `0202-09` would otherwise be a legitimate request for a month
 * eighteen centuries ago.
 */
export function isValidYearMonth(value: string): boolean {
  if (!/^\d{4}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5));
  return year >= 2000 && year <= 2100 && month >= 1 && month <= 12;
}

/**
 * First and last calendar day of a `YYYY-MM` month, at UTC midnight (DATE-3).
 *
 * `Date.UTC(y, m, 0)` is the last day of month `m` — the zeroth day of the next
 * one — which is what keeps February right without naming a leap year rule.
 */
export function monthBounds(yearMonthKey: string): { first: Date; last: Date } {
  const year = Number(yearMonthKey.slice(0, 4));
  const month = Number(yearMonthKey.slice(5));
  return {
    first: new Date(Date.UTC(year, month - 1, 1)),
    last: new Date(Date.UTC(year, month, 0)),
  };
}

/** The `YYYY-MM` key a date falls in — what a month picker holds. */
export function yearMonth(d: Date): string {
  return ymd(d).slice(0, 7);
}

/** Every calendar day of a `YYYY-MM` month. The row axis of a month report. */
export function eachDayOfMonth(yearMonthKey: string): Date[] {
  const { first, last } = monthBounds(yearMonthKey);
  const days: Date[] = [];
  for (let d = first; d.getTime() <= last.getTime(); d = addDays(d, 1)) days.push(d);
  return days;
}

/**
 * "Mon" — the weekday alone.
 *
 * Not `formatDay`, which carries the date too: a report with its own date column
 * would print it twice.
 */
export function weekdayName(d: Date): string {
  return WEEKDAYS[d.getUTCDay()]!;
}

/** The first day (UTC midnight) of the month containing `d`. */
export function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

const monthLabelFormatter = new Intl.DateTimeFormat("en-AU", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/**
 * "September 2026" — the way a month is named to a person, from its `YYYY-MM` key.
 *
 * Formatted at UTC because the key names a calendar month, not an instant; running
 * it through a timezone is how the 1st slides into the previous month.
 */
export function monthLabel(yearMonthKey: string): string {
  return monthLabelFormatter.format(monthBounds(yearMonthKey).first);
}

/**
 * One timesheet period: a contiguous run of days inside one week **and** one
 * month (TIME-1). For most weeks that is the whole week; a week straddling a
 * month boundary is two periods, so the earlier month can be approved and
 * frozen while the later month's days stay fillable.
 */
export type WeekSegment = {
  /** The week's Monday — shared by both halves of a split week. */
  weekStart: Date;
  periodStart: Date;
  periodEnd: Date;
  /** `YYYY-MM` the period falls in. */
  month: string;
};

/**
 * The one or two periods of the week starting `monday`, in date order.
 *
 * The only place a week is cut at a month boundary (DATE-4). Both halves keep
 * the week's Monday, so every lock keyed on the Monday — TIME-4's future bound,
 * TIME-5's age lock, RATE-4's rate in force — treats them alike.
 */
export function weekSegments(monday: Date): WeekSegment[] {
  const weekStart = startOfWeek(monday);
  const weekEnd = addDays(weekStart, 6);
  if (weekStart.getUTCMonth() === weekEnd.getUTCMonth()) {
    return [{ weekStart, periodStart: weekStart, periodEnd: weekEnd, month: yearMonth(weekStart) }];
  }
  const firstOfNext = startOfMonth(weekEnd);
  return [
    {
      weekStart,
      periodStart: weekStart,
      periodEnd: addDays(firstOfNext, -1),
      month: yearMonth(weekStart),
    },
    { weekStart, periodStart: firstOfNext, periodEnd: weekEnd, month: yearMonth(weekEnd) },
  ];
}

/** The period a given day belongs to. */
export function segmentFor(date: Date): WeekSegment {
  const t = utcDate(date).getTime();
  return weekSegments(startOfWeek(date)).find(
    (s) => t >= s.periodStart.getTime() && t <= s.periodEnd.getTime()
  )!;
}

/** Every calendar day of a period, in order. */
export function periodDays(segment: Pick<WeekSegment, "periodStart" | "periodEnd">): Date[] {
  const days: Date[] = [];
  for (let d = segment.periodStart; d.getTime() <= segment.periodEnd.getTime(); d = addDays(d, 1)) {
    days.push(d);
  }
  return days;
}

/**
 * The period of the week starting `monday` that begins on `periodStart` — the
 * week's first period when none is named, which for a whole week is the week.
 * Null when `periodStart` does not begin one of the week's periods: a client
 * naming a day in the middle of a period is naming nothing.
 */
export function weekSegment(monday: Date, periodStart?: Date | null): WeekSegment | null {
  const segments = weekSegments(monday);
  if (!periodStart) return segments[0]!;
  const t = utcDate(periodStart).getTime();
  return segments.find((s) => s.periodStart.getTime() === t) ?? null;
}

/** Whether a period is its whole week — true for eleven weeks in twelve. */
export function isWholeWeek(period: Pick<WeekSegment, "weekStart" | "periodStart" | "periodEnd">): boolean {
  return (
    period.periodStart.getTime() === period.weekStart.getTime() &&
    period.periodEnd.getTime() === addDays(period.weekStart, 6).getTime()
  );
}

/**
 * Whether a period holds a weekday. One that does not — Sunday 1 November, the
 * tail of a week that starts in October — is a period nobody is expected to
 * file: a reminder or a chase about it would be about a weekend.
 */
export function periodHasWeekday(period: Pick<WeekSegment, "periodStart" | "periodEnd">): boolean {
  return periodDays(period).some((d) => !isWeekend(d));
}

/** e.g. "28 Sep – 30 Sep 2026", or "04 Oct 2026" for a one-day period. */
export function periodRange(periodStart: Date, periodEnd: Date): string {
  const day = (d: Date) => `${String(d.getUTCDate()).padStart(2, "0")} ${MONTHS[d.getUTCMonth()]}`;
  if (periodStart.getTime() === periodEnd.getTime()) return `${day(periodEnd)} ${periodEnd.getUTCFullYear()}`;
  return `${day(periodStart)} – ${day(periodEnd)} ${periodEnd.getUTCFullYear()}`;
}

/**
 * e.g. "Wk 40 · 28 Sep – 30 Sep 2026". A whole week reads exactly as
 * `weekLabel` does, so the eleven weeks in twelve that never split look the
 * same as they always have.
 */
export function periodLabel(periodStart: Date, periodEnd: Date): string {
  return `Wk ${isoWeek(periodStart)} · ${periodRange(periodStart, periodEnd)}`;
}

/**
 * An Australian BAS quarter: the financial year runs July to June and is named
 * by the year it ends in, so FY2027 is July 2026 – June 2027, and its Q1 is
 * July–September 2026. Keyed "FY2027-Q1" — sortable as text, since the year
 * leads and the quarter is one digit.
 */
export type BasQuarter = {
  key: string;
  financialYear: number;
  quarter: 1 | 2 | 3 | 4;
  /** First and last calendar day, at UTC midnight (DATE-3). */
  start: Date;
  end: Date;
  /** "Jul–Sep 2026" — how a person names it. */
  label: string;
};

const QUARTER_MONTHS = ["Jul–Sep", "Oct–Dec", "Jan–Mar", "Apr–Jun"] as const;

function basQuarter(financialYear: number, quarter: 1 | 2 | 3 | 4): BasQuarter {
  // Q1 starts in July of the year before the one the FY is named for.
  const startMonth = (6 + 3 * (quarter - 1)) % 12;
  const startYear = quarter <= 2 ? financialYear - 1 : financialYear;
  const start = new Date(Date.UTC(startYear, startMonth, 1));
  const end = new Date(Date.UTC(startYear, startMonth + 3, 0));
  return {
    key: `FY${financialYear}-Q${quarter}`,
    financialYear,
    quarter,
    start,
    end,
    label: `${QUARTER_MONTHS[quarter - 1]} ${startYear}`,
  };
}

/** The BAS quarter a calendar date falls in. */
export function basQuarterOf(date: Date): BasQuarter {
  const month = date.getUTCMonth(); // 0 = January
  const year = date.getUTCFullYear();
  const financialYear = month >= 6 ? year + 1 : year;
  const quarter = (month >= 6 ? Math.floor((month - 6) / 3) + 1 : Math.floor(month / 3) + 3) as 1 | 2 | 3 | 4;
  return basQuarter(financialYear, quarter);
}

/** "FY2027-Q1" → its quarter, or null for anything that is not one. */
export function parseBasQuarter(key: string): BasQuarter | null {
  const match = /^FY(\d{4})-Q([1-4])$/.exec(key);
  if (!match) return null;
  const financialYear = Number(match[1]);
  if (financialYear < 2001 || financialYear > 2100) return null;
  return basQuarter(financialYear, Number(match[2]) as 1 | 2 | 3 | 4);
}

/** Every BAS quarter from `first` to `last` inclusive, in order. */
export function basQuartersBetween(first: BasQuarter, last: BasQuarter): BasQuarter[] {
  const out: BasQuarter[] = [];
  for (let q = first; q.start.getTime() <= last.start.getTime(); q = basQuarterOf(addDays(q.end, 1))) {
    out.push(q);
  }
  return out;
}

export const RANGE_PRESET_KEYS = ["THIS_MONTH", "LAST_MONTH", "THIS_QUARTER", "LAST_QUARTER"] as const;
export type RangePresetKey = (typeof RANGE_PRESET_KEYS)[number];

export type RangePreset = {
  key: RangePresetKey;
  label: string;
  /** Inclusive `YYYY-MM-DD` bounds, as a From/To filter holds them. */
  from: string;
  to: string;
};

/**
 * The quick ranges a list offers beside its From/To: this and last month, this
 * and last quarter, from the business's `today` — pass `businessDate()`, never
 * `new Date()`. Quarters are the BAS quarters, which are calendar quarters.
 */
export function rangePresets(today: Date): RangePreset[] {
  const thisMonth = monthBounds(yearMonth(today));
  const lastMonth = monthBounds(yearMonth(addDays(thisMonth.first, -1)));
  const thisQuarter = basQuarterOf(today);
  const lastQuarter = basQuarterOf(addDays(thisQuarter.start, -1));
  return [
    { key: "THIS_MONTH", label: "This month", from: ymd(thisMonth.first), to: ymd(thisMonth.last) },
    { key: "LAST_MONTH", label: "Last month", from: ymd(lastMonth.first), to: ymd(lastMonth.last) },
    { key: "THIS_QUARTER", label: "This quarter", from: ymd(thisQuarter.start), to: ymd(thisQuarter.end) },
    { key: "LAST_QUARTER", label: "Last quarter", from: ymd(lastQuarter.start), to: ymd(lastQuarter.end) },
  ];
}
